import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Ghost as GhostIcon, Sparkles, ShoppingBag, User as UserIcon, LogOut, LayoutDashboard, Menu, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useCart } from "@/hooks/useCart";
import { useAuth } from "@/hooks/useAuth";

const links = [
  { label: "Bohaterowie", to: "/#bohaterowie", id: "nav-bohaterowie" },
  { label: "Książki", to: "/#ksiazki", id: "nav-ksiazki" },
  { label: "Pomoce", to: "/#pomoce", id: "nav-pomoce" },
  { label: "Sklep", to: "/sklep", id: "nav-sklep", route: true },
  { label: "Quiz", to: "/quiz", id: "nav-quiz", route: true },
];

export default function Nav() {
  const location = useLocation();
  const onHome = location.pathname === "/";
  const { count, openCart } = useCart();
  const { user, logout } = useAuth();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <motion.header
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      className="fixed top-4 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-6xl"
      data-testid="site-nav"
    >
      <nav className="flex items-center justify-between gap-4 px-4 md:px-6 py-3 rounded-full backdrop-blur-xl bg-white/70 border border-white/70 shadow-[0_20px_60px_-20px_rgba(11,42,85,0.25)]">
        <Link to="/" className="flex items-center gap-2 tk-link" data-testid="brand-logo">
          <motion.span
            className="w-9 h-9 rounded-full bg-[#84C5F4] grid place-items-center text-white"
            whileHover={{ rotate: [0, -15, 15, -8, 0], scale: 1.1 }}
            transition={{ duration: 0.5 }}
          >
            <GhostIcon size={20} strokeWidth={2.5} />
          </motion.span>
          <span className="font-display text-lg font-semibold text-[#051937]">
            Tonek <span className="font-script text-[#60A5FA] text-2xl leading-none">i</span> Duszek
          </span>
        </Link>

        <ul className="hidden lg:flex items-center gap-6 text-sm font-semibold text-[#0b2a55]">
          {links.map((l) => (
            <li key={l.id}>
              {l.route || !onHome ? (
                <Link to={l.to} data-testid={l.id} className="animated-underline">
                  {l.label}
                </Link>
              ) : (
                <a href={l.to.replace("/", "")} data-testid={l.id} className="animated-underline">
                  {l.label}
                </a>
              )}
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-2">
          <motion.button
            onClick={openCart}
            data-testid="nav-cart"
            whileHover={{ scale: 1.06 }}
            whileTap={{ scale: 0.94 }}
            className="relative w-10 h-10 rounded-full bg-white border border-[#BAE6FD] hover:bg-[#F4FAFF] grid place-items-center text-[#051937]"
          >
            <ShoppingBag size={16} />
            {count > 0 && (
              <motion.span
                key={count}
                initial={{ scale: 0.5 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 400, damping: 15 }}
                className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-[#EF4444] text-white text-[10px] font-bold grid place-items-center px-1"
                data-testid="nav-cart-count"
              >
                {count}
              </motion.span>
            )}
          </motion.button>

          {user ? (
            <div className="relative">
              <motion.button
                onClick={() => setUserMenuOpen((v) => !v)}
                data-testid="nav-user"
                whileHover={{ scale: 1.05 }}
                className="flex items-center gap-2 rounded-full bg-white border border-[#BAE6FD] hover:bg-[#F4FAFF] pl-1 pr-3 py-1 text-sm font-semibold text-[#051937]"
              >
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                ) : (
                  <span className="w-8 h-8 rounded-full bg-[#FDE68A] grid place-items-center">
                    <UserIcon size={14} className="text-[#051937]" />
                  </span>
                )}
                <span className="hidden md:inline max-w-[100px] truncate">
                  {user.name || user.email?.split("@")[0]}
                </span>
              </motion.button>

              <AnimatePresence>
                {userMenuOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setUserMenuOpen(false)} />
                    <motion.div
                      initial={{ opacity: 0, y: -8, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -8, scale: 0.95 }}
                      transition={{ duration: 0.15 }}
                      className="absolute right-0 top-full mt-2 w-56 rounded-2xl bg-white border border-[#BAE6FD] shadow-[0_20px_60px_-20px_rgba(11,42,85,0.35)] p-2 z-50"
                    >
                      <div className="px-3 py-2 border-b border-[#BAE6FD]/60">
                        <div className="font-semibold text-[#051937] text-sm truncate">{user.name || "Użytkownik"}</div>
                        <div className="text-xs text-[#0b2a55]/60 truncate">{user.email}</div>
                      </div>
                      {user.role === "admin" && (
                        <Link
                          to="/admin"
                          onClick={() => setUserMenuOpen(false)}
                          data-testid="nav-admin-link"
                          className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-[#F4FAFF] text-sm font-semibold text-[#051937]"
                        >
                          <LayoutDashboard size={14} /> Panel administratora
                        </Link>
                      )}
                      <button
                        onClick={() => { setUserMenuOpen(false); logout(); }}
                        data-testid="nav-logout"
                        className="w-full flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-[#FEE2E2] text-sm font-semibold text-[#DC2626]"
                      >
                        <LogOut size={14} /> Wyloguj
                      </button>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
          ) : (
            <Link
              to="/login"
              data-testid="nav-login"
              className="hidden md:inline-flex items-center gap-2 rounded-full bg-white border border-[#BAE6FD] hover:bg-[#F4FAFF] text-[#051937] px-4 py-2 text-sm font-semibold"
            >
              <UserIcon size={14} /> Zaloguj
            </Link>
          )}

          <Link
            to="/sklep"
            data-testid="nav-cta"
            className="group hidden md:inline-flex items-center gap-2 rounded-full bg-[#051937] hover:bg-[#0b2a55] text-white px-4 py-2 text-sm font-semibold transition-colors"
          >
            <Sparkles size={16} className="group-hover:rotate-12 transition-transform" />
            Do sklepu
          </Link>

          {/* Mobile menu */}
          <button
            onClick={() => setMobileOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            className="lg:hidden w-10 h-10 rounded-full bg-white border border-[#BAE6FD] grid place-items-center text-[#051937]"
          >
            {mobileOpen ? <X size={16} /> : <Menu size={16} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            className="lg:hidden mt-2 rounded-3xl bg-white/95 backdrop-blur-xl border border-[#BAE6FD] p-4 shadow-[0_20px_60px_-20px_rgba(11,42,85,0.25)]"
          >
            <ul className="flex flex-col gap-3 text-sm font-semibold text-[#051937]">
              {links.map((l) => (
                <li key={l.id}>
                  <Link
                    to={l.to}
                    onClick={() => setMobileOpen(false)}
                    className="block px-3 py-2 rounded-xl hover:bg-[#F4FAFF]"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
              {!user && (
                <li>
                  <Link
                    to="/login"
                    onClick={() => setMobileOpen(false)}
                    className="block px-3 py-2 rounded-xl bg-[#051937] text-white text-center"
                  >
                    Zaloguj się
                  </Link>
                </li>
              )}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
